import type { WardStockOption } from '../types/wardstock.type'

// MAR statuses that take stock from a ward-stock lot or the patient's dispensed supply.
export const STOCK_CONSUMING_MAR_STATUSES = ['administered', 'late_administered', 'wasted'] as const

export const consumesStock = (status?: string | null): boolean =>
  !!status && (STOCK_CONSUMING_MAR_STATUSES as readonly string[]).includes(status)

// MAR statuses that need a typed reason (the backend answers 422 reason_required when the note is blank).
export const REASON_REQUIRED_MAR_STATUSES = ['refused', 'wasted'] as const

export const requiresReason = (status?: string | null): boolean =>
  !!status && (REASON_REQUIRED_MAR_STATUSES as readonly string[]).includes(status)

type BuildMarUpdatePayloadArgs = {
  status: string
  quantity?: number | string | null
  note?: string | null
  source?: Pick<WardStockOption, 'id' | 'source_type'>
  staffId?: number | string | null
}

/**
 * JSON:API body for PATCH /medication_administrations/:id.
 * - quantity is exactly what the nurse entered (never the lot's stock), and only for stock-consuming statuses
 * - the stock relationship is sent only when a source was chosen: ward_stock -> product_inventory, else dispensing
 * - ids are never the string "undefined"
 */
export const buildMarUpdatePayload = ({ note, quantity, source, staffId, status }: BuildMarUpdatePayloadArgs) => {
  const takesStock = consumesStock(status)
  const hasQuantity = quantity !== undefined && quantity !== null && `${quantity}` !== ''

  const stockRelationship =
    takesStock && source
      ? source.source_type === 'ward_stock'
        ? { product_inventory: { data: { id: `${source.id}`, type: 'product_inventories' } } }
        : { dispensing: { data: { id: `${source.id}`, type: 'dispensing' } } }
      : {}

  const hasStaff = staffId !== undefined && staffId !== null && `${staffId}` !== ''

  return {
    data: {
      attributes: {
        note: note ?? undefined,
        quantity: takesStock && hasQuantity ? Number(quantity) : undefined,
        status
      },
      relationships: {
        ...stockRelationship,
        ...(hasStaff ? { staff: { data: { id: `${staffId}`, type: 'staff' } } } : {})
      },
      type: 'medication_administration'
    }
  }
}
